import { useEffect, useRef } from 'react';
import { useSendbirdStateContext } from '@sendbird/uikit-react/useSendbirdStateContext';
import { GroupChannelHandler, HiddenState } from '@sendbird/chat/groupChannel';

/**
 * Chat v2 keeps the open conversation in a single Redux field (`chat.channelUrl`),
 * while the channel list is rendered by Sendbird UIKit's `<GroupChannelList>` and
 * self-heals from the SDK's internal collection. When the current user is removed
 * from a group (another participant bans them), the list drops the channel but
 * `channelUrl` is never touched, so the conversation panel keeps rendering the
 * now-stale channel (DA-19493).
 *
 * When the currently-open channel becomes inaccessible to the current user this hook
 * deselects it via `onSelect('')` — the same programmatic-deselect path each layout
 * already passes to `ChannelList` (mirrors the delete-channel flow). Delegating to the
 * layout's own handler lets each layout do the right thing: desktop reverts to the
 * empty panel, while mobile (single-pane) navigates back to the list
 * (`handleSelect('')` → `handleBackToList`) instead of stranding the user on a blank
 * screen with no way back.
 *
 * Triggers:
 *   - `onUserBanned`  — the removal path (`banUserWithUserId`) the ticket describes,
 *   - `onUserLeft`    — the current user leaves the open channel,
 *   - `onChannelDeleted` — the open channel is deleted for everyone,
 *   - `onChannelHidden` (`hidden_prevent_auto_unhide`) — the channel is hidden server-side (e.g.
 *      a document-scoped chat when its document is archived/deleted). This variant drops the
 *      channel from the default (UNHIDDEN) list query and does NOT auto-reappear on a new message,
 *      so the open panel would otherwise be stranded on a channel no longer in the list.
 *
 * NOT triggered on freeze alone: a frozen channel still appears in the list (read-only), so it
 * should stay selected. (In practice the server hides + freezes together; the hide branch covers it.)
 *
 * The deselect is silent (no toast) per the parent story P0-10: "No notification is
 * sent to the removed participant." The handler is re-registered whenever the open
 * channel changes (so its comparison always sees the current URL) and removed on
 * unmount / sdk change, so it never leaks or fires twice.
 */
const HANDLER_KEY_PREFIX = 'chat-clear-open-channel-on-removal';

const useClearOpenChannelOnRemoval = (openChannelUrl: string, onSelect: (url: string) => void): void => {
  const sdk = useSendbirdStateContext().stores.sdkStore.sdk;

  // Unique per hook instance: the Sendbird SDK singleton is shared, so a fixed key would
  // let two mounted layouts (e.g. the global drawer + mobile approvals) overwrite each
  // other's handler via addGroupChannelHandler. React 17 has no useId; generate it lazily
  // inside the effect (never during render) and keep it stable in a ref across re-runs.
  const handlerKeyRef = useRef<string>('');

  useEffect(() => {
    if (!sdk?.groupChannel) return undefined;

    if (!handlerKeyRef.current) {
      handlerKeyRef.current = `${HANDLER_KEY_PREFIX}-${Math.random().toString(36).slice(2)}`;
    }
    const handlerKey = handlerKeyRef.current;

    const deselectIfOpen = (channelUrl: string): void => {
      if (channelUrl === openChannelUrl) {
        onSelect('');
      }
    };

    const handler = new GroupChannelHandler({
      onUserBanned: (channel, user) => {
        if (user.userId === sdk.currentUser?.userId) deselectIfOpen(channel.url);
      },
      onUserLeft: (channel, user) => {
        if (user.userId === sdk.currentUser?.userId) deselectIfOpen(channel.url);
      },
      onChannelDeleted: (channelUrl) => deselectIfOpen(channelUrl),
      onChannelHidden: (channel) => {
        if (channel.hiddenState === HiddenState.HIDDEN_PREVENT_AUTO_UNHIDE) deselectIfOpen(channel.url);
      },
    });

    sdk.groupChannel.addGroupChannelHandler(handlerKey, handler);

    return () => {
      sdk.groupChannel.removeGroupChannelHandler(handlerKey);
    };
  }, [sdk, openChannelUrl, onSelect]);
};

export default useClearOpenChannelOnRemoval;
